import { Link } from 'react-router-dom';
import { locations } from '../../mocks/locations';

const HERO_IMAGE = 'https://greenworldil.com/wp-content/uploads/2025/06/Shingled-Roof-repair-Chicago.webp';
const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2968.3!2d-87.8059!3d41.9847!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880fcf594a21d73d%3A0x29c65c88f0ec9b5d!2s5801%20N%20Northwest%20Hwy%2C%20Chicago%2C%20IL%2060631!5e0!3m2!1sen!2sus!4v1710000000000';

export default function LocationsPage() {
  const featured = locations.filter((l) => l.featured);
  const others = locations.filter((l) => !l.featured);

  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex items-end min-h-64 pt-40 pb-16"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-map-pin-2-fill text-brand-gold text-xs" />
            <span className="text-brand-gold text-xs font-semibold uppercase tracking-widest">Service Areas</span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-3">
            Roofing Services Across<br />
            <span className="text-brand-gold">Chicago &amp; Chicagoland</span>
          </h1>
          <p className="text-white/80 max-w-xl leading-relaxed">
            Proudly serving homeowners and businesses across Chicago and 24+ suburbs. Local expertise for local properties — since 1989.
          </p>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-brand-dark py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {['Local Service', 'Proven Results', '24+ Communities', 'Free Inspections'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </section>

      {/* Map + intro */}
      <section className="bg-brand-cream py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <span className="text-brand-green text-xs font-bold uppercase tracking-widest">Chicago-Based Contractor</span>
              <h2 className="font-display text-4xl font-bold text-brand-dark mt-3 mb-5">
                We Come to You — Anywhere<br />
                <span className="text-brand-green">in Chicagoland</span>
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm">
                Green World Construction is based at 5801 N Northwest Highway in Chicago and services the entire Chicago metro area. Our crews know the neighborhoods, the building stock, and the specific challenges that Chicago&apos;s climate creates for every type of roofing system.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                Whether you&apos;re in a 1920s two-flat in Lakeview or a commercial property in the western suburbs, we bring the same standard of craftsmanship and transparency to every job.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-bold px-7 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap">
                Check Availability in Your Area
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="rounded-lg overflow-hidden border border-brand-cream-3 h-80">
              <iframe src={MAP_EMBED} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Green World Construction Chicago location" />
            </div>
          </div>

          {/* Featured locations */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-7">
              <h2 className="font-display text-2xl font-bold text-brand-dark">Primary Service Cities</h2>
              <div className="h-px bg-brand-cream-3 flex-1" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featured.map((loc) => (
                <Link
                  key={loc.id}
                  to={`/locations/${loc.slug}`}
                  className="group bg-white rounded-lg p-6 border border-brand-cream-3 hover:border-brand-gold transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-lg">
                      <i className="ri-map-pin-2-fill text-brand-green text-lg" />
                    </div>
                    <span className="text-xs text-brand-gold font-semibold bg-brand-gold/10 px-2.5 py-1 rounded-full">Primary</span>
                  </div>
                  <h3 className="font-semibold text-brand-dark mb-1">{loc.city}, {loc.state}</h3>
                  <p className="text-xs text-gray-500 mb-4">{loc.county} &bull; Roofing, Tuckpointing, Siding &amp; More</p>
                  <span className="text-xs font-bold text-brand-green flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Services in {loc.city} <i className="ri-arrow-right-line text-xs" />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* All other locations */}
          <div>
            <div className="flex items-center gap-3 mb-7">
              <h2 className="font-display text-2xl font-bold text-brand-dark">All Service Areas</h2>
              <div className="h-px bg-brand-cream-3 flex-1" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {others.map((loc) => (
                <Link
                  key={loc.id}
                  to={`/locations/${loc.slug}`}
                  className="bg-white rounded-lg px-4 py-3 border border-brand-cream-3 hover:border-brand-gold transition-all flex items-center gap-2 cursor-pointer group"
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-brand-green text-xs" />
                  </div>
                  <span className="text-sm text-gray-700 group-hover:text-brand-green transition-colors font-medium truncate">{loc.city}, IL</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-green py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">Serving Your Chicago Neighborhood</h2>
          <p className="text-white/80 mb-7">Call us or schedule online — we&apos;ll confirm service availability and book your free inspection.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Schedule Free Inspection
            </Link>
            <a href="tel:7086984072" className="border-2 border-white/40 hover:border-white text-white text-sm font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-phone-fill" />
              (708) 698-4072
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
